/* Searched image bytes live in IndexedDB, not a resident Blob/decoded-image cache.
   Leases belong to displayed images; parked views keep only their original URL. */
(function (global) {
    'use strict';
    const TTL_MS = 3 * 60 * 60 * 1000;
    const PREVIEW_EDGE = 960;
    const workQueue = [];
    let activeWork = 0;
    const DB_NAME = 'MemoraReferenceImagesDB';
    const pending = new Map();
    const mounted = new WeakMap();
    const urls = new Set();
    let cleanupTimer = 0;
    let nextCleanupAt = Infinity;
    let dbPromise = null;
    let storageFailures = 0;
    let remoteFallbacks = 0;

    function openDb() {
        if (dbPromise) return dbPromise;
        dbPromise = new Promise((resolve, reject) => {
            const request = global.indexedDB.open(DB_NAME, 1);
            request.onupgradeneeded = () => {
                const store = request.result.createObjectStore('images', { keyPath: 'url' });
                store.createIndex('expiresAt', 'expiresAt');
            };
            request.onsuccess = () => {
                const db = request.result;
                db.onversionchange = () => { db.close(); dbPromise = null; };
                resolve(db);
            };
            request.onerror = request.onblocked = () => reject(request.error || new Error('Image cache is unavailable.'));
        }).catch((error) => { dbPromise = null; storageFailures++; throw error; });
        return dbPromise;
    }

    async function read(url) {
        const db = await openDb();
        return new Promise((resolve, reject) => {
            const request = db.transaction('images').objectStore('images').get(url);
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }

    async function write(record) {
        const db = await openDb();
        await new Promise((resolve, reject) => {
            const tx = db.transaction('images', 'readwrite');
            tx.objectStore('images').put(record);
            tx.oncomplete = resolve;
            tx.onerror = tx.onabort = () => reject(tx.error || new Error('Image cache write failed.'));
        });
        scheduleCleanup(record.expiresAt);
    }
    function cleanUrl(value) {
        try {
            const url = new URL(value);
            return /^https?:$/.test(url.protocol) ? url.href : '';
        } catch (_error) { return ''; }
    }

    async function purge() {
        const db = await openDb();
        return new Promise((resolve, reject) => {
            let deleted = 0;
            const tx = db.transaction('images', 'readwrite');
            const index = tx.objectStore('images').index('expiresAt');
            const request = index.openCursor(global.IDBKeyRange.upperBound(Date.now()));
            request.onsuccess = () => {
                const cursor = request.result;
                if (!cursor) {
                    const next = index.openKeyCursor(global.IDBKeyRange.lowerBound(Date.now(), true));
                    next.onsuccess = () => { if (next.result) scheduleCleanup(Number(next.result.key)); };
                    return;
                }
                cursor.delete(); deleted++;
                cursor.continue();
            };
            tx.oncomplete = () => resolve(deleted);
            tx.onerror = tx.onabort = () => reject(tx.error || new Error('Reference image cleanup failed.'));
        });
    }

    function scheduleCleanup(expiresAt) {
        if (!Number.isFinite(expiresAt) || expiresAt >= nextCleanupAt) return;
        global.clearTimeout(cleanupTimer);
        nextCleanupAt = expiresAt;
        cleanupTimer = global.setTimeout(() => {
            cleanupTimer = 0;
            nextCleanupAt = Infinity;
            void purge().catch(() => {});
        }, Math.max(1, expiresAt - Date.now()));
    }

    async function withImageWork(signal, work) {
        await new Promise((resolve, reject) => {
            const entry = { signal, run: () => { signal?.removeEventListener('abort', abort); activeWork++; resolve(); } };
            const abort = () => {
                const index = workQueue.indexOf(entry);
                if (index >= 0) workQueue.splice(index, 1);
                reject(new DOMException('Image view closed.', 'AbortError'));
            };
            if (signal?.aborted) { abort(); return; }
            signal?.addEventListener('abort', abort, { once: true });
            if (activeWork < 2) entry.run(); else workQueue.push(entry);
        });
        try {
            if (signal?.aborted) throw new DOMException('Image view closed.', 'AbortError');
            return await work();
        } finally {
            activeWork--;
            if (workQueue.length) workQueue.shift().run();
        }
    }

    async function makePreview(blob, edge, signal) {
        const check = () => { if (signal?.aborted) throw new DOMException('Image view closed.', 'AbortError'); };
        check();
        // Read common raster dimensions without decoding a full-sized browser Image first.
        const bytes = new Uint8Array(await blob.slice(0, 65536).arrayBuffer());
        const data = new DataView(bytes.buffer);
        let sourceWidth = 0, sourceHeight = 0;
        if (bytes.length >= 24 && data.getUint32(0) === 0x89504e47 && data.getUint32(4) === 0x0d0a1a0a) {
            sourceWidth = data.getUint32(16); sourceHeight = data.getUint32(20);
        } else if (bytes[0] === 0xff && bytes[1] === 0xd8) {
            for (let offset = 2; offset + 8 < bytes.length;) {
                if (bytes[offset++] !== 0xff) break;
                while (bytes[offset] === 0xff) offset++;
                const marker = bytes[offset++];
                if (marker === 0xd9 || marker === 0xda) break;
                if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd8)) continue;
                const size = data.getUint16(offset);
                if ([0xc0,0xc1,0xc2,0xc3,0xc5,0xc6,0xc7,0xc9,0xca,0xcb,0xcd,0xce,0xcf].includes(marker)) {
                    sourceHeight = data.getUint16(offset + 3); sourceWidth = data.getUint16(offset + 5); break;
                }
                if (size < 2) break;
                offset += size;
            }
        }
        let bitmap = null, image = null, objectUrl = '', canvas = null;
        try {
            check();
            const scale = sourceWidth && sourceHeight ? Math.min(1, edge / sourceWidth, edge / sourceHeight) : 1;
            try {
                bitmap = await global.createImageBitmap(blob, sourceWidth && sourceHeight
                    ? { resizeWidth: Math.max(1, Math.round(sourceWidth * scale)), resizeHeight: Math.max(1, Math.round(sourceHeight * scale)), resizeQuality: 'high' }
                    : {});
            } catch (error) {
                check();
                objectUrl = global.URL.createObjectURL(blob);
                image = new global.Image();
                await new Promise((resolve, reject) => {
                    const finish = (error) => { image.onload = image.onerror = null; signal?.removeEventListener('abort', abort); error ? reject(error) : resolve(); };
                    const abort = () => { image.removeAttribute('src'); finish(new DOMException('Image view closed.', 'AbortError')); };
                    signal?.addEventListener('abort', abort, { once: true });
                    image.onload = () => finish(); image.onerror = () => finish(error);
                    image.src = objectUrl;
                });
            }
            check();
            const drawable = bitmap || image;
            sourceWidth ||= drawable.width || drawable.naturalWidth;
            sourceHeight ||= drawable.height || drawable.naturalHeight;
            const factor = Math.min(1, edge / (drawable.width || drawable.naturalWidth), edge / (drawable.height || drawable.naturalHeight));
            canvas = global.document.createElement('canvas');
            canvas.width = Math.max(1, Math.round((drawable.width || drawable.naturalWidth) * factor));
            canvas.height = Math.max(1, Math.round((drawable.height || drawable.naturalHeight) * factor));
            canvas.getContext('2d').drawImage(drawable, 0, 0, canvas.width, canvas.height);
            const previewBlob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
            check();
            if (!previewBlob) throw new Error('Image preview could not be encoded.');
            return { previewBlob, sourceWidth, sourceHeight, previewWidth: canvas.width, previewHeight: canvas.height };
        } finally {
            bitmap?.close(); image?.removeAttribute('src');
            if (objectUrl) global.URL.revokeObjectURL(objectUrl);
            if (canvas) { canvas.width = 0; canvas.height = 0; }
        }
    }

    async function readOrFetch(url, signal) {
        let saved;
        try { saved = await read(url); } catch (_error) { }
        if (signal.aborted) return null;
        if (saved?.expiresAt > Date.now()) {
            scheduleCleanup(saved.expiresAt);
            if (saved.blob instanceof Blob && !saved.previewBlob) {
                try {
                    const preview = await withImageWork(signal, () => makePreview(saved.blob, PREVIEW_EDGE, signal));
                    saved = { ...saved, ...preview };
                    await write(saved); // An older record's fixed expiry is never extended.
                } catch (error) { if (signal.aborted) throw error; }
            }
            return saved;
        }
        if (saved) void purge().catch(() => {});
        return withImageWork(signal, async () => {
        let blob = null;
        try {
            const response = await global.fetch(url, {
                mode: 'cors', credentials: 'omit', signal, referrerPolicy: 'no-referrer-when-downgrade'
            });
            if (response.ok) {
                const downloaded = await response.blob();
                if (downloaded.size && /^image\//i.test(downloaded.type)) blob = downloaded;
            }
        } catch (_error) { /* A host without CORS still permits an ordinary image display. */ }
        if (signal.aborted) return null;
        const createdAt = Date.now();
        let preview = {};
        if (blob) {
            try { preview = await makePreview(blob, PREVIEW_EDGE, signal); }
            catch (error) { if (signal.aborted) throw error; }
        }
        if (signal.aborted) return null;
        const record = { url, blob, ...preview, byteLength: blob?.size || 0, cacheStatus: blob ? 'stored' : 'remote-only',
            createdAt, expiresAt: createdAt + TTL_MS };
        try {
            // Even inaccessible hosts retain their URL record for three hours; never store an opaque
            // response as though it were usable bytes. The metadata makes the fallback inspectable.
            await write(record);
        } catch (_error) { storageFailures++; }
        return record;
        });
    }

    async function acquire(value, { signal, maxDimension = 0 } = {}) {
        const url = cleanUrl(value);
        if (!url) return { url: String(value || ''), cached: false, release() {} };
        if (signal?.aborted) throw new DOMException('Image view closed.', 'AbortError');
        let job = pending.get(url);
        if (!job) {
            const controller = new AbortController();
            const timeout = global.setTimeout(() => controller.abort(), 10000);
            job = { controller, users: 0, promise: null };
            job.promise = readOrFetch(url, controller.signal).finally(() => {
                global.clearTimeout(timeout);
                if (pending.get(url) === job) pending.delete(url);
            });
            pending.set(url, job);
        }
        job.users++;
        let returned = false;
        const leave = () => {
            if (returned) return;
            returned = true;
            if (--job.users === 0 && pending.get(url) === job) {
                pending.delete(url);
                job.controller.abort();
            }
        };
        signal?.addEventListener('abort', leave, { once: true });
        let record;
        try { record = await job.promise; }
        finally { signal?.removeEventListener('abort', leave); leave(); }
        if (signal?.aborted) throw new DOMException('Image view closed.', 'AbortError');
        if (!record?.blob) { remoteFallbacks++; return { url, cached: false, release() {} }; }
        let blob = record.blob;
        if (maxDimension > 0) {
            if (maxDimension <= PREVIEW_EDGE && record.previewBlob) blob = record.previewBlob;
            else {
                const preview = await withImageWork(signal, () => makePreview(blob, maxDimension, signal));
                blob = preview.previewBlob;
            }
        }
        if (signal?.aborted) throw new DOMException('Image view closed.', 'AbortError');
        const objectUrl = global.URL.createObjectURL(blob);
        urls.add(objectUrl);
        let released = false;
        return {
            url: objectUrl, cached: true, width: record.sourceWidth || 0, height: record.sourceHeight || 0,
            release() {
                if (released) return;
                released = true;
                urls.delete(objectUrl);
                global.URL.revokeObjectURL(objectUrl);
            }
        };
    }

    function images(root) {
        if (!root) return [];
        return root.matches?.('img[data-memora-reference-src]') ? [root]
            : Array.from(root.querySelectorAll?.('img[data-memora-reference-src]') || []);
    }

    function isDisplayed(image) {
        return image.isConnected && !image.closest('[hidden],[data-memora-stage-suspended],#ask-ai-modal[aria-hidden="true"]');
    }

    const observed = new WeakSet();
    const offscreen = new WeakSet();
    const visibility = typeof global.IntersectionObserver === 'function' ? new global.IntersectionObserver((entries) => {
        for (const entry of entries) {
            if (entry.isIntersecting) { offscreen.delete(entry.target); void mount(entry.target); }
            else { offscreen.add(entry.target); release(entry.target, true); }
        }
    }, { rootMargin: '200px' }) : null;

    function mount(root) {
        return Promise.all(images(root).map((image) => {
            if (!isDisplayed(image)) { release(image); return; }
            if (visibility && !observed.has(image)) { observed.add(image); visibility.observe(image); }
            if (offscreen.has(image)) return;
            const existing = mounted.get(image);
            if (existing) return existing.ready;
            const state = { controller: new AbortController(), lease: null, ready: null };
            mounted.set(image, state);
            state.ready = acquire(image.dataset.memoraReferenceSrc, { signal: state.controller.signal, maxDimension: PREVIEW_EDGE }).then((lease) => {
                if (state.controller.signal.aborted || !isDisplayed(image) || mounted.get(image) !== state) {
                    lease.release();
                    return;
                }
                state.lease = lease;
                image.dataset.memoraReferenceCache = lease.cached ? 'stored' : 'remote-only';
                image.src = lease.url;
            }).catch(() => {}).finally(() => { state.ready = null; });
            return state.ready;
        }));
    }

    function release(root, keepObserved = false) {
        for (const image of images(root)) {
            if (!keepObserved) { visibility?.unobserve(image); observed.delete(image); offscreen.delete(image); }
            const state = mounted.get(image);
            state?.controller.abort();
            state?.lease?.release();
            mounted.delete(image);
            image.removeAttribute('src');
            image.removeAttribute('srcset');
        }
    }

    global.MemoraReferenceImageCache = Object.freeze({
        ttlMs: TTL_MS, dbName: DB_NAME, acquire, mount, release, purge,
        stats: () => ({ activeObjectUrls: urls.size, pendingDownloads: pending.size, activeDecodes: activeWork, storageFailures, remoteFallbacks })
    });
    // Added/removed Ask and gallery nodes use the same lifecycle, including late search completions.
    const observer = new global.MutationObserver((records) => {
        for (const record of records) {
            if (record.type === 'attributes') {
                if (record.attributeName !== 'aria-hidden' || record.target.id === 'ask-ai-modal') void mount(record.target);
                continue;
            }
            for (const node of record.removedNodes) if (!node.isConnected) release(node);
            for (const node of record.addedNodes) if (node.isConnected) void mount(node);
        }
    });
    if (global.document.body) observer.observe(global.document.body, { childList: true, subtree: true, attributes: true,
        attributeFilter: ['hidden', 'data-memora-stage-suspended', 'aria-hidden'] });
    global.addEventListener('pageshow', () => { void purge().catch(() => {}); });
    global.addEventListener('focus', () => { void purge().catch(() => {}); });
    void purge().catch(() => {});
}(window));
